import { useState } from "react";
import type {
  CalendarRow,
  ClubView,
  ContinentalView,
  CupRunView,
  CupStageView,
  CupTieView,
} from "../game/types";
import { Crest, Panel, PositionChip, StrengthBadge } from "../ui/atoms";
import { ChaveDaCopa } from "../ui/ChaveDaCopa";
import { StandingsTable, TablePanel } from "./panels";
import { rotuloDeFase, rotuloDoMotor, useT, type Traduzir } from "../i18n";

type Aba = "calendario" | "tabela" | "estadual" | "copa" | "continental" | "artilharia";

/**
 * Competições: tabela da liga, chave da copa e artilharia.
 *
 * O chaveamento é remontado a partir dos jogos, porque o motor só guarda os
 * confrontos da fase em andamento. A artilharia é recortada pela liga do clube,
 * senão um artilheiro de quarta divisão encabeça a lista de quem joga a elite.
 */
export function CompetitionsPanel({
  view,
  onOpenPlayer,
}: {
  view: ClubView;
  onOpenPlayer: (playerId: string) => void;
}) {
  const t = useT();
  const [aba, setAba] = useState<Aba>("calendario");

  const abas: Array<[Aba, string]> = [
    ["calendario", t("competicoes.abaCalendario")],
    ["tabela", view.club.leagueName],
    ["estadual", view.state?.name ?? t("competicoes.abaEstadual")],
    ["copa", view.cup?.name ?? t("competicoes.abaCopa")],
    ["continental", view.continental?.name ?? t("competicoes.abaContinental")],
    ["artilharia", t("competicoes.abaArtilharia")],
  ];

  return (
    <div className="grid" style={{ gap: 14 }}>
      <div className="row" style={{ gap: 8, flexWrap: "wrap" }}>
        {abas.map(([key, label]) => (
          <button
            key={key}
            className="ghost"
            onClick={() => setAba(key)}
            disabled={
              (key === "copa" && !view.cup) ||
              (key === "continental" && !view.continental) ||
              (key === "estadual" && !view.state)
            }
            style={
              aba === key
                ? { borderColor: "var(--primary)", color: "var(--primary)", fontWeight: 700 }
                : undefined
            }
          >
            {label}
          </button>
        ))}
      </div>

      {aba === "calendario" && <Calendario view={view} />}
      {aba === "tabela" && <TablePanel view={view} />}
      {aba === "estadual" && view.state && (
        <StandingsTable
          title={view.state.name}
          rows={view.state.rows}
          action={
            <span className="label-muted">
              {view.state.championName
                ? t("competicoes.campeao", { clube: view.state.championName })
                : t("competicoes.estadualResumo")}
            </span>
          }
          maxHeight="calc(100vh - 300px)"
        />
      )}
      {aba === "copa" && view.cup && <CupBracket cup={view.cup} myClubId={view.club.id} />}
      {aba === "continental" && view.continental && (
        <Continental continental={view.continental} myClubId={view.club.id} />
      )}
      {aba === "artilharia" && <Scorers view={view} onOpenPlayer={onOpenPlayer} />}
    </div>
  );
}

/**
 * Calendário da temporada.
 *
 * Mistura os quatro torneios numa lista só, em ordem de rodada, porque é assim
 * que a temporada chega para quem dirige o clube. O estadual some no meio da
 * liga e da copa exatamente como some na vida real, e é essa mistura que
 * explica por que a quarta rodada tem três jogos em oito dias.
 *
 * A lista abre rolada no próximo jogo em vez de no começo do ano: quem abre o
 * calendário quer saber o que vem, não o que já passou.
 */
function Calendario({ view }: { view: ClubView }) {
  const t = useT();
  const [torneio, setTorneio] = useState<string>("todos");

  const proximo = view.calendar.find((jogo) => !jogo.played) ?? null;
  const torneios = [...new Set(view.calendar.map((jogo) => jogo.competition))];
  const lista =
    torneio === "todos" ? view.calendar : view.calendar.filter((j) => j.competition === torneio);
  const classicos = view.calendar.filter((jogo) => jogo.derby).length;

  return (
    <Panel
      title={t("competicoes.abaCalendario")}
      action={
        <span className="label-muted">
          {t("competicoes.calendario.resumo", {
            jogos: String(view.calendar.length),
            classicos: String(classicos),
          })}
        </span>
      }
      padded={false}
    >
      {torneios.length > 1 && (
        <div
          className="row"
          style={{
            gap: 6,
            flexWrap: "wrap",
            padding: "10px 12px",
            borderBottom: "1px solid var(--border)",
          }}
        >
          {[t("competicoes.calendario.todos"), ...torneios].map((nome, indice) => {
            const chave = indice === 0 ? "todos" : nome;
            const ativo = torneio === chave;
            return (
              <button
                key={chave}
                className="ghost"
                onClick={() => setTorneio(chave)}
                style={
                  ativo
                    ? { borderColor: "var(--primary)", color: "var(--primary)", fontWeight: 700 }
                    : undefined
                }
              >
                {nome}
              </button>
            );
          })}
        </div>
      )}

      {lista.length === 0 ? (
        <p className="muted" style={{ padding: "18px 12px", margin: 0 }}>
          {t("competicoes.calendario.vazio")}
        </p>
      ) : (
        <div className="scroll" style={{ maxHeight: "calc(100vh - 320px)" }}>
          <ul style={{ listStyle: "none", margin: 0, padding: 0 }}>
            {lista.map((jogo) => (
              <LinhaDoCalendario
                key={jogo.id}
                jogo={jogo}
                proximo={proximo !== null && jogo.id === proximo.id}
              />
            ))}
          </ul>
        </div>
      )}
    </Panel>
  );
}

/** Uma partida do calendário: quando, contra quem, onde e como terminou. */
function LinhaDoCalendario({ jogo, proximo }: { jogo: CalendarRow; proximo: boolean }) {
  const t = useT();
  // Pontos corridos não têm fase, e aí o nome da competição já diz tudo.
  const fase = rotuloDeFase(jogo.stage);

  // Vitória, empate e derrota pedem cores diferentes, e o placar só existe
  // depois de jogado. Antes disso o lugar do placar mostra onde é o jogo, que é
  // a informação que importa para quem está montando a semana.
  const cor =
    jogo.goalsFor === null || jogo.goalsAgainst === null
      ? "var(--text-muted)"
      : jogo.goalsFor > jogo.goalsAgainst
        ? "var(--success)"
        : jogo.goalsFor < jogo.goalsAgainst
          ? "var(--danger)"
          : "var(--text-muted)";

  return (
    <li
      ref={(elemento) => {
        if (proximo && elemento) elemento.scrollIntoView({ block: "center" });
      }}
      className="row"
      style={{
        gap: 10,
        alignItems: "center",
        padding: "9px 12px",
        borderBottom: "1px solid var(--border)",
        background: proximo ? "var(--surface-2)" : undefined,
        opacity: jogo.played ? 0.72 : 1,
      }}
    >
      <span className="label-muted" style={{ width: 30, textAlign: "right", flexShrink: 0 }}>
        {jogo.round}
      </span>

      <Crest colors={jogo.opponentColors} short={jogo.opponentShort} size={22} />

      <div style={{ flex: 1, minWidth: 0 }}>
        <div className="row" style={{ gap: 6, alignItems: "center" }}>
          <strong
            style={{
              fontSize: 13,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {jogo.opponent}
          </strong>
          {jogo.derby && (
            <span
              className="label"
              title={jogo.derbyLabelKey ? rotuloDoMotor(jogo.derbyLabelKey) : undefined}
              style={{
                color: "var(--warning)",
                border: "1px solid var(--warning)",
                borderRadius: 4,
                padding: "0 5px",
                fontSize: 10,
                flexShrink: 0,
              }}
            >
              {jogo.derbyLabelKey
                ? rotuloDoMotor(jogo.derbyLabelKey)
                : t("competicoes.abaCalendario")}
            </span>
          )}
          {proximo && (
            <span className="label" style={{ color: "var(--primary)", flexShrink: 0 }}>
              {t("competicoes.calendario.proximo")}
            </span>
          )}
        </div>
        <span
          className="label-muted"
          style={{
            display: "block",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {jogo.competition}
          {fase ? `, ${fase}` : ""}
        </span>
      </div>

      <span style={{ color: cor, fontWeight: 700, fontSize: 13, flexShrink: 0 }}>
        {jogo.played
          ? `${jogo.goalsFor} x ${jogo.goalsAgainst}`
          : jogo.neutral
            ? t("competicoes.calendario.neutro")
            : jogo.home
              ? t("competicoes.calendario.casa")
              : t("competicoes.calendario.fora")}
      </span>
    </li>
  );
}

/**
 * Continental: os grupos primeiro, o mata-mata depois.
 *
 * Enquanto a fase de grupos corre, o grupo do clube do jogador aparece no topo
 * e os outros ficam recolhidos, senão oito tabelas seguidas empurram a
 * informação que interessa para fora da tela.
 */
function Continental({
  continental,
  myClubId,
}: {
  continental: ContinentalView;
  myClubId: string;
}) {
  const t = useT();
  const [verGrupos, setVerGrupos] = useState(false);
  const meu = continental.groups.find((group) => group.mine);
  const outros = continental.groups.filter((group) => !group.mine);
  const emGrupos = continental.phase === "grupos";

  // Na fase de grupos o que importa é a tabela do clube do jogador, com os
  // outros grupos recolhidos. Depois dela o que importa é o chaveamento, e são
  // os grupos que passam a ficar recolhidos. Oito tabelas empurrariam a final
  // para fora da tela.
  const grupos = emGrupos && !verGrupos && meu ? [meu] : [meu, ...outros].filter(Boolean);
  const tabelas =
    continental.groups.length === 0 ? null : (
      <div className="grid" style={{ gap: 10 }}>
        {grupos.map(
          (group) =>
            group && (
              <StandingsTable
                key={group.name}
                title={rotuloDeFase(group.name)}
                rows={group.rows}
                qualify={2}
                action={
                  group.mine ? <span className="label">{t("competicoes.seuGrupo")}</span> : undefined
                }
              />
            ),
        )}
      </div>
    );

  return (
    <div className="grid" style={{ gap: 14 }}>
      <Panel
        title={continental.name}
        action={
          <span className="label-muted">
            {continental.championName
              ? t("competicoes.campeao", { clube: continental.championName })
              : campanha(t, continental.name, continental.myStatus)}
          </span>
        }
      >
        <p className="muted" style={{ margin: 0, fontSize: 12 }}>
          {continental.championName
            ? t("competicoes.tituloDaEdicao", { clube: continental.championName })
            : emGrupos
              ? t("competicoes.doisPrimeiros")
              : t("competicoes.gruposEncerrados")}
        </p>
      </Panel>

      {emGrupos ? (
        <>
          {tabelas}
          {meu && outros.length > 0 && (
            <button className="ghost" onClick={() => setVerGrupos((v) => !v)}>
              {verGrupos
                ? t("competicoes.mostrarSoMeuGrupo")
                : t("competicoes.verOutrosGrupos", { total: outros.length })}
            </button>
          )}
        </>
      ) : (
        <>
          <CupBracket cup={continental} myClubId={myClubId} embedded />
          {continental.groups.length > 0 && (
            <button className="ghost" onClick={() => setVerGrupos((v) => !v)}>
              {verGrupos ? t("competicoes.esconderGrupos") : t("competicoes.verGrupos")}
            </button>
          )}
          {verGrupos && tabelas}
        </>
      )}
    </div>
  );
}

/**
 * A campanha do clube numa copa, escrita.
 *
 * A visão entrega a situação, não a frase: ela é montada dentro do Web Worker,
 * que nunca soube o idioma escolhido. Escrever aqui é o que faz a linha sair em
 * inglês para quem joga em inglês.
 */
function campanha(t: Traduzir, competicao: string, run: CupRunView): string {
  return t(`competicoes.campanha.${run.situacao}`, {
    competicao,
    clube: run.clube,
    fase: rotuloDeFase(run.fase),
    grupo: rotuloDeFase(run.grupo),
  });
}

function CupBracket({
  cup,
  myClubId,
  embedded = false,
}: {
  cup: NonNullable<ClubView["cup"]>;
  myClubId: string;
  /** Dentro da continental o cabeçalho já existe acima e não se repete. */
  embedded?: boolean;
}) {
  const t = useT();
  const played = cup.stages.filter((stage) => stage.ties.length > 0);

  return (
    <Panel
      title={embedded ? t("competicoes.chaveamento") : cup.name}
      action={
        embedded ? undefined : (
          <span className="label-muted">
            {cup.championName
              ? t("competicoes.campeao", { clube: cup.championName })
              : campanha(t, cup.name, cup.myStatus)}
          </span>
        )
      }
    >
      {played.length === 0 ? (
        <p className="muted" style={{ margin: 0 }}>
          {t("competicoes.copaNaoComecou")}
        </p>
      ) : (
        <ChaveDaCopa
          stages={cup.stages}
          myClubId={myClubId}
          aindaEmLista={(fases) => (
            <div className="grid" style={{ gap: 18 }}>
              {fases.map((stage) => (
                <Stage key={stage.key} stage={stage} myClubId={myClubId} />
              ))}
            </div>
          )}
        />
      )}
    </Panel>
  );
}

function Stage({ stage, myClubId }: { stage: CupStageView; myClubId: string }) {
  const t = useT();
  const formato = stage.legs === 2 ? t("competicoes.idaEVolta") : t("competicoes.jogoUnico");
  const situacao =
    stage.status === "atual" ? `${formato} · ${t("competicoes.emDisputa")}` : formato;
  return (
    <section>
      <div className="row" style={{ gap: 8, marginBottom: 8 }}>
        <span className="label">{rotuloDeFase(stage.key)}</span>
        <span className="label-muted">{situacao}</span>
      </div>
      <div className="grid" style={{ gap: 6 }}>
        {stage.ties.slice(0, 16).map((tie, index) => (
          <Tie
            key={`${tie.homeId}-${tie.awayId}-${index}`}
            tie={tie}
            legs={stage.legs}
            myClubId={myClubId}
          />
        ))}
        {stage.ties.length > 16 && (
          <span className="label-muted">
            {t("competicoes.maisConfrontos", { total: stage.ties.length - 16 })}
          </span>
        )}
      </div>
    </section>
  );
}

function Tie({ tie, legs, myClubId }: { tie: CupTieView; legs: number; myClubId: string }) {
  const t = useT();
  const decided = tie.winnerId !== null;
  const playedLegs = tie.legs.filter((leg) => leg.played).length;
  const allPlayed = tie.legs.length >= legs && tie.legs.every((leg) => leg.played);
  const onPenalties = tie.legs.some((leg) => leg.shootoutWinner !== null);

  const side = (
    id: string,
    name: string,
    short: string,
    colors: [string, string],
    goals: number,
    alignRight: boolean,
  ) => {
    const winner = decided && tie.winnerId === id;
    const mine = id === myClubId;
    const crest = <Crest colors={colors} short={short} size={26} />;
    const label = (
      <span
        style={{
          flex: 1,
          minWidth: 0,
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
          textAlign: alignRight ? "right" : "left",
          fontWeight: winner || mine ? 700 : 400,
          color: decided && !winner ? "var(--text-dim)" : "inherit",
        }}
      >
        {name}
      </span>
    );
    const score = (
      <span
        className="num"
        style={{
          fontWeight: 800,
          minWidth: 16,
          textAlign: "center",
          color: winner ? "var(--primary)" : decided ? "var(--text-dim)" : "inherit",
        }}
      >
        {playedLegs > 0 ? goals : t("comum.vazio")}
      </span>
    );
    return (
      <div className="row" style={{ gap: 8, flex: 1, minWidth: 0 }}>
        {alignRight ? (
          <>
            {score}
            {label}
            {crest}
          </>
        ) : (
          <>
            {crest}
            {label}
            {score}
          </>
        )}
      </div>
    );
  };

  // Um 2 a 2 decidido na marca do pênalti parece empate sem resolução se a tela
  // não disser. O rótulo do meio é onde isso cabe.
  //
  // O rótulo também precisa da fase, não só das pernas já criadas: com a volta
  // ainda por marcar, um confronto de ida e volta tem uma perna só e seria lido
  // como jogo único decidido.
  const middle = !allPlayed
    ? legs > 1 && playedLegs === 1
      ? t("competicoes.ida")
      : t("competicoes.aDecidir")
    : onPenalties
      ? t("competicoes.penaltis")
      : legs > 1
        ? t("competicoes.agregado")
        : t("competicoes.jogoUnico");

  return (
    <div
      className="row"
      style={{
        gap: 12,
        padding: "8px 12px",
        borderRadius: 8,
        background: tie.mine ? "var(--primary-glow)" : "var(--surface-2)",
        border: tie.mine ? "1px solid var(--primary)" : "1px solid transparent",
      }}
    >
      {side(tie.homeId, tie.homeName, tie.homeShort, tie.homeColors, tie.aggHome, false)}
      <span
        className="label-muted"
        style={{ whiteSpace: "nowrap", color: onPenalties ? "var(--accent)" : undefined }}
      >
        {middle}
      </span>
      {side(tie.awayId, tie.awayName, tie.awayShort, tie.awayColors, tie.aggAway, true)}
    </div>
  );
}

function Scorers({
  view,
  onOpenPlayer,
}: {
  view: ClubView;
  onOpenPlayer: (playerId: string) => void;
}) {
  const t = useT();
  if (view.scorers.length === 0) {
    return (
      <Panel title={t("competicoes.artilharia")}>
        <p className="muted" style={{ margin: 0 }}>
          {t("competicoes.ninguemMarcou")}
        </p>
      </Panel>
    );
  }

  return (
    <Panel
      title={t("competicoes.artilhariaDa", { liga: view.club.leagueName })}
      action={
        <span className="label-muted">
          {t("competicoes.quantosNaLista", { total: view.scorers.length })}
        </span>
      }
      padded={false}
    >
      <div className="scroll" style={{ maxHeight: "60vh" }}>
        <table>
          <thead>
            <tr>
              <th className="right" style={{ width: 34 }}>
                {t("competicoes.colunasArtilharia.posicao")}
              </th>
              <th style={{ width: 48 }}>{t("competicoes.colunasArtilharia.pos")}</th>
              <th>{t("competicoes.colunasArtilharia.jogador")}</th>
              <th>{t("competicoes.colunasArtilharia.clube")}</th>
              <th className="right" style={{ width: 44 }}>
                {t("competicoes.colunasArtilharia.jogos")}
              </th>
              <th className="right" style={{ width: 52 }}>
                {t("competicoes.colunasArtilharia.assistencias")}
              </th>
              <th className="right" style={{ width: 44 }}>
                {t("competicoes.colunasArtilharia.gols")}
              </th>
            </tr>
          </thead>
          <tbody>
            {view.scorers.map((scorer, index) => (
              <tr
                key={scorer.playerId}
                className="player"
                onClick={() => onOpenPlayer(scorer.playerId)}
                style={{
                  cursor: "pointer",
                  background: scorer.mine ? "var(--primary-glow)" : "transparent",
                }}
              >
                <td className="right num" style={{ color: "var(--text-dim)" }}>
                  {index + 1}
                </td>
                <td>
                  <PositionChip pos={scorer.pos} />
                </td>
                <td style={{ fontWeight: scorer.mine ? 700 : 600, whiteSpace: "nowrap" }}>
                  {scorer.name}
                </td>
                <td>
                  <span className="row" style={{ gap: 8 }}>
                    <span
                      style={{
                        width: 4,
                        height: 16,
                        borderRadius: 2,
                        background: scorer.clubColors[0],
                        display: "inline-block",
                      }}
                    />
                    <span className="muted" style={{ fontSize: 11.5, whiteSpace: "nowrap" }}>
                      {scorer.clubName}
                    </span>
                  </span>
                </td>
                <td className="right num">{scorer.apps}</td>
                <td className="right num">{scorer.assists || ""}</td>
                <td className="right">
                  <StrengthBadge value={scorer.goals} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Panel>
  );
}
